import { IAction } from "../../../base/common/actions.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { isProxyInvariantError } from "../../../base/common/errors.js";
import { ILocalizedString } from "../../action/common/action.js";
import { CommandService } from "../../commands/common/commandService.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import { SetPropertyCommand } from "./propertyCommands.js";
import { IEqualityComparer, PropertyComparisonService } from "./propertyComparison.js";
import { IValueConverter, ValueConversionService, ValueConverterOptions as ConverterContext } from "./converter.js";
import { isColorLike, isColorPropertyLike, isVectorPropertyLike } from "./types.js";
import { IPropertyAccessorService } from "./accessor.js";
import { Disposable, IDisposable } from "../../../base/common/lifecycle.js";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage.js";
import { IAttachment } from "../../../base/common/attachment.js";
import { PropertyAccessorService } from "./accessorService.js";
import { URI } from "../../../base/common/uri.js";
import { ContextKeyExpr, ContextKeyExpression, IContextKeyService } from "../../contextkey/common/contextkey.js";
import { ILogService } from "../../log/common/log.js";
import { memberReadDefinition, type PropertyReadDefinition } from "./propertyReadDefinition.js";

// #region Property Service

export const IPropertyService = createDecorator<IPropertyService>("propertyService");

export const enum PropertyScope {
    INSPECTOR,
    AI_QUERY,
}

export enum PropertyViewMode {
    DEFAULT,
    ALL,
};

export interface GetPropertyOptions {
    scope: PropertyScope;
    viewMode: PropertyViewMode;
}

export interface IPropertyService {

    readonly _serviceBrand: undefined;
    readonly propertyChanged: Event<PropertyChangedEvent>;

    getProperties(object: object, options: GetPropertyOptions): Promise<IPropertyCollection | null>;
    findProperty(address: PropertyAddress): Promise<IProperty | null>;
    registerProvider(provider: IPropertyProvider): void;
    unregisterProvider(provider: IPropertyProvider): void;
}

export interface IPropertyStateProvider {
    isHidden(object: object, key: string): boolean;
    setHidden?(object: object, key: string, hidden: boolean): void;
}

export interface IPropertyProvider {
    canHandle(object: object): boolean;
    getProperties(object: Record<string, unknown>, options: GetPropertyOptions): Promise<IPropertyCollection | null>;
    findProperty(address: PropertyAddress): Promise<IProperty | null>;
    createProperty(object: Record<string, unknown>, key: string): IProperty | null;
}




// #region Utils

export namespace PropertyUtils {

    export function getAllObjectProperties(obj: any) {
        const allDescriptors: Record<string, PropertyDescriptor> = {};
        let current = obj;
        while (current && current !== Object.prototype) {
            const descriptors = Object.getOwnPropertyDescriptors(current);
            // Don't overwrite - instance properties take precedence
            Object.keys(descriptors).forEach(key => {
                if (!(key in allDescriptors)) {
                    allDescriptors[key] = descriptors[key];
                }
            });
            current = Object.getPrototypeOf(current);
        }

        return allDescriptors;
    }

    export function findPropertyDescriptor(obj: any, key: string): PropertyDescriptor | null {
        let current = obj;
        while (current && current !== Object.prototype) {
            const descriptor = Object.getOwnPropertyDescriptor(current, key);
            if (descriptor) {
                return descriptor;
            }
            current = Object.getPrototypeOf(current);
        }
        return null;
    }

}



// #region Property

/**
 * A unique address to identify a property within an object.
 */
export interface PropertyAddress {
    objectId: string;
    /** Owner already resolved for this lookup; never stored as document identity. */
    object?: object;
    /**
     * The path to the property within the object, e.g., "transform.position.x".
     * Indexes for arrays can be specified using square brackets, e.g., "materials[0].color".
     */
    propertyPath: string;
}

export function isPropertyAddress(obj: unknown): obj is PropertyAddress {
    if (!obj) return false;
    return typeof obj === "object"
        && 'objectId' in obj && typeof (obj as any).objectId === "string"
        && 'propertyPath' in obj && typeof (obj as any).propertyPath === "string";
}


export type PropertyType = "function" | "string" | "number" | "boolean" | "object" | "color" | "vector" | "matrix" | "video";
/** Video properties display a URL or Blob with native playback controls. */
export type VideoPropertyValue = string | Blob | null;

type SetValueArguments<T> = {
    /**
     * The new value to set for the property.
     */
    newValue: T,
    /**
     * The old value of the property, if known. If provided it will be used for undo
     */
    oldValue?: T,
    /**
     * If true, the change notification will not be sent after setting the value.
     */
    withoutNotify?: boolean,
    /**
     * If true, the change will not be recorded in the undo stack.
     */
    withoutUndo?: boolean,

    /**
     * Optional metadata associated with this value change.
     */
    metadata?: ValueMetadata,

    /**
     * Hint for value conversion, e.g., "skybox" for HDRi textures that need equirectangular mapping.
     * Passed through to accessors/converters to guide how values are processed.
     */
    usageHint?: "skybox" | string,

    /**
     * Internal: skip {@link IProperty.applyGuard}. Set by SetPropertyCommand's own
     * apply/undo (which are downstream of the guarded user write), so those don't
     * re-trigger the guard. User-initiated writes leave it unset and are gated.
     */
    skipApplyGuard?: boolean,
};

/**
 * Metadata that can be provided when setting a property value.
 */
export interface ValueMetadata {
    /**
     * The source of the value, e.g., "user", "system", "ai", etc.
     */
    source?: string;

    /**
     * The filename associated with the value, if applicable.
     */
    filename?: string;

    /**
     * The URI associated with the value, if applicable.
     */
    uri?: string;

    /**
     * The value as a string, if applicable.
     */
    // IMPORTANT: only primitive types allowed here because it's used for changesets and should be easily serializable and no heavy objects
    value?: null | string | boolean | number
    | { x?: number, y?: number, z?: number, w?: number }
    | [number, number] | [number, number, number]
    | [number, number, number, number];

    /**
     * The target of the value assignment: e.g., "material.diffuseMap", "object.position", etc.
     */
    target?: string;

    /**
     * The SOURCE ASSET a value comes from (library/provider assign — e.g. an HDRI url
     * set on scene.background). Lets the ONE command emission point capture the assign
     * with its asset identity, regardless of which UI path invoked the command.
     */
    asset?: { id?: string; provider?: string; src: string };
}

export interface PropertyMetadataExtraData {
    value: Record<string, string | number | boolean | PropertyMetadataExtraData>;
}

/**
 * Metadata for a property, such as min/max values for numbers.
 */
export interface PropertyMetadata {
    /** Missing frames in the video result, including capture misses and encoder drops. */
    videoMissedFrames?: number;

    /** Whether this edit belongs to the persistent scene document or to local
     * editor state. Editor-scoped values still use commands for undo, but must
     * not enter document capture, sync, or the scene changeset. */
    editScope?: "document" | "editor";

    /**
     * The unique address of the property. This should provide a full path to the property within its owner object.
     */
    address?: string;

    /**
     * The SCENE OBJECT the address is rooted at, when the edited thing is reached
     * THROUGH one (a material/texture row on a mesh: "218.material.color").
     * `address` only carries that object's session id, which is useless to a
     * reader — a changeset row said "218.material.color" while a transform edit on
     * the same mesh said "/Scene/Group/Group/Mesh". Recording the object lets
     * consumers show where the edit was made, which also matters because
     * materials are SHARED: the id says which mesh you edited it on.
     * Absent for orphan-inspected elements (no owning object).
     */
    addressOwner?: object;

    priority?: number; // higher priority properties are shown first

    description?: string | ILocalizedString;

    docsUrl?: string; // URL to documentation for this property

    /**
     * Structured type metadata (resolved from TypeScript declarations by the
     * modern-monaco type service) — powers type tooltips, enum pickers, …
     */
    typeInfo?: PropertyTypeInfo;

    /**
     * Optional lazy type metadata resolver. Tooltip/UI code can call this on
     * demand so providers do not have to block property collection on network
     * type loading.
     */
    resolveTypeInfo?: (options?: { signal?: AbortSignal }) => Promise<PropertyTypeInfo | undefined>;

    /** Indicates that this property value is meant to never change */
    static?: boolean;

    /**
     * Indicates if editing for this property should be disabled
     */
    editable?: boolean;

    /**
     * Indicates that this property can not be read
     */
    readable?: boolean;
    /**
     * Indicates that this property can not be written to
     */
    writeable?: boolean;

    /**
     * Indicates that this property should be hidden from the UI
     */
    hidden?: boolean;

    /** Minimum value for the property (if applicable) */
    min?: number;
    /** Maximum value for the property (if applicable) */
    max?: number;
    /** Step value for the property (if applicable) */
    step?: number;
    /** Maximum displayed decimal places for number inputs (0–100). Does not round stored values. Omit for full precision. */
    precision?: number;

    colorspace?:
    // The colorspace for color properties supported by the browser's <input type="color">
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input/color
    "limited-srgb" | "display-p3"
    ;

    alpha?: boolean; // whether the color includes alpha channel

    /**
     * The accepted types for this property. This can be used to indicate special handling in the UI.   
     * Multiple URI types can be specified separated by commas: e.g., "null, image/*, color/*"
     */
    accept?: "null" | "image/*" | "color/*" | ({} & string); // e.g., MIME types like "image/*"

    /**
     * The default value for the property. This can be given by the user or system. It is set when the property is built.
     */
    defaultValue?: any;

    /**
     * The initial value for the property when first created. This can differ from defaultValue if the property is resettable.
     */
    initialValue?: any;

    /**
     * If the property is an enum, the possible options for the enum.
     */
    enumPresentation?: "datalist";
    enumOptions?: Array<{
        label: string | ILocalizedString;
        description?: string | ILocalizedString;
        /** Optional outline preview in the enum datalist; auto means a dynamic aspect. */
        aspectRatio?: number | "auto";
        value: any, hidden?: boolean,
    }>;

    /**
     * If true a multi-select input field should be used. Requires enumOptions to be set.
     */
    multiple?: boolean;

    /**
     * If true, prefer to not create an undo step when changing this property (e.g. 'material.needsUpdate').
     */
    preferNoUndo?: boolean;

    /**
     * A placeholder string to show when the property value is an object and has no special UI yet
     */
    objectValuePlaceholderString?: string;

    /**
     * Can be used to render extra UI for the property (e.g. for mesh information)
     */
    // extraData?: PropertyMetadataExtraData,


    /**
     * The context key for this property (e.g. material.map). Can be used with `when` to control visibility.
     */
    contextKey?: string;

    /**
     * Optional value transform used when publishing this property's contextKey.
     * This is useful when dependent properties care about a derived boolean/enum
     * rather than the raw object value.
     */
    contextValue?: (value: unknown) => unknown;

    /**
     * A context key expression that indicates when this property should be visible
     */
    when?: ContextKeyExpression;

    /**
     * Wether to inset the property in the UI (e.g., for nested properties). It can be used for visual grouping.
     */
    inset?: boolean;

    /**
     * When set the property might be shown in a details view or similar.
     */
    detailsId?: string;

    /**
     * When detailsId is set, this label can be used for the details link/button.
     */
    detailsLabel?: string;

    /**
     * Optional label for nested child properties rendered under this property.
     */
    childrenLabel?: string;

    /**
     * Controls whether nested child properties are currently expanded in the inspector.
     * If undefined, the inspector treats the children as expanded by default.
     */
    childrenExpanded?: boolean;

    /**
     * Picker configuration. When provided a picker UI should be shown for the property.
     */
    picker?: {
        id: string,
        prefix: string,
    }

    /**
     * Set on the child properties of an "eventlist" property: which callback entry the
     * child belongs to and which part of the entry it edits. The EventList inspector
     * widget groups its row controls by this.
     */
    eventCall?: {
        index: number;
        role: "enabled" | "target" | "method" | "argument";
    };

    /**
     * For vector properties, the number of components (e.g., 2 for vec2, 3 for vec3, etc.)
     */
    constrainedProportions?: boolean;
}

// #region property type metadata (resolved lazily from TypeScript declarations
// by the modern-monaco type service; see platform/typescript/)
export interface PropertyTypeInfo {
    packageName?: string;
    packageVersion?: string;
    moduleName?: string;
    ownerType: string;
    name: string;
    type: string;
    readonly?: boolean;
    optional?: boolean;
    description?: string;
    defaultValue?: string;
    sourceUrl?: string;
    enumOptions?: Array<{ label: string; value: string | number }>;
    container?: PropertyContainerTypeInfo;
    constructor?: PropertyConstructorTypeInfo;
    objectMembers?: PropertyObjectMemberTypeInfo[];
}

export interface PropertyContainerTypeInfo {
    kind: "array" | "map" | "set" | "object";
    elementType?: string;
    elementTypeName?: string;
    elementConstructor?: PropertyConstructorTypeInfo;
    elementMembers?: PropertyObjectMemberTypeInfo[];
    keyType?: string;
    valueType?: string;
    valueTypeName?: string;
    valueConstructor?: PropertyConstructorTypeInfo;
    valueMembers?: PropertyObjectMemberTypeInfo[];
}

export interface PropertyObjectMemberTypeInfo {
    name: string;
    type: string;
    readonly?: boolean;
    optional?: boolean;
    constructor?: PropertyConstructorTypeInfo;
    objectMembers?: PropertyObjectMemberTypeInfo[];
}

export interface PropertyConstructorTypeInfo {
    typeName: string;
    parameters: Array<{
        name: string;
        type: string;
        optional?: boolean;
        rest?: boolean;
    }>;
}
// #endregion

export const PropertyMetadata = new class {

    /**
     * Tests whether the given property or metadata accepts the given type(s).  
     * @param obj The property or metadata to test.
     * @param accept The accept type(s) to test for.
     * @returns True if the property/metadata accepts the given type(s), false otherwise. If the property/metadata has no accept field, returns undefined.
     * @example 
     * ```ts
     * // Check if a property accepts image types
     * const acceptsImages = PropertyMetadata.testAccept(myProperty, "image/*");
     * 
     * // Check if a metadata accepts color or image types
     * const acceptsColorOrImage = PropertyMetadata.testAccept(myProperty.metadata, "color/*, image/*");
     * ```
     */
    testAccept(obj: IProperty | Pick<PropertyMetadata, "accept">, accept: string): boolean | undefined {
        if (isPropertyLike(obj)) {
            obj = obj.metadata;
        }
        if (obj.accept === undefined) {
            return false;
        }

        // We loop through each accept type and see if any match
        const testArray = accept.split(",").map(a => a.trim());
        const propertyAccepts = obj.accept.split(",").map(a => a.trim());

        for (const test of testArray) {
            const testPrefix = test.endsWith("/*") ? test.substring(0, test.length - 1) : null;

            for (const accept of propertyAccepts) {
                if (accept === test) {
                    return true;
                }
                // Regex match for wildcard types
                if (accept.endsWith("/*")) {
                    // e.g. "image/*" -> "image/"
                    const prefix = accept.substring(0, accept.length - 1);
                    // check if accept starts with this prefix
                    if (test.startsWith(prefix)) {
                        return true;
                    }
                }
                if (testPrefix) {
                    if (accept.startsWith(testPrefix)) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
}



export interface PropertyChangedEvent {
    property: IProperty;

    /**
     * Indicates whether the property value actually changed.
     * If undefined, the change may be structural (e.g., metadata change).
     */
    value?: boolean;

    /**
     * The previous value of the property, if known.
     */
    previousValue?: any;

    /**
     * True when only the property's VISIBILITY changed (`hidden` toggled by
     * `when`-clause bookkeeping during collection rebuilds). The value did not
     * change — listeners that track value edits (project edits, texture
     * version bumps, …) must ignore these.
     */
    hiddenOnly?: boolean;

    /**
     * True for an INTERMEDIATE preview write during a continuous edit
     * (`previewValue` — a live drag/type tick), as opposed to the final
     * `commitEdit`. Cheap consumers (viewport uniform upload) act on every
     * event; EXPENSIVE ones (shader recompile, changeset, persistence) should
     * skip transient events and act only on the commit.
     */
    transient?: boolean;
}


export interface IProperty {
    readonly onDidChange: Event<PropertyChangedEvent>;
    notifyChanged(args?: Omit<PropertyChangedEvent, "property">): void;

    readonly key: string;
    readonly type: PropertyType | ({} & string); // e.g., "string", "number", "boolean", "color", etc.
    readonly owner: object; // the object that owns this property
    readonly metadata: PropertyMetadata;

    /**
     * Whether the property is readable
     */
    get editable(): boolean;
    set editable(value: boolean);

    /**
     * Whether the property is readable
     */
    get hidden(): boolean;
    set hidden(value: boolean);

    label: string; // human-readable label

    /**
     * Nested properties belonging to this property, e.g. texture settings under a texture slot.
     */
    children?: IProperty[];

    /**
     * Indicates whether the property value is different from its default value.
     */
    valueNotDefault?(): boolean | undefined;
    /** The displayed value is inherited and has no authored override. */
    isInherited?(): boolean;
    resetToDefault?(): Promise<boolean> | undefined;

    get value(): unknown;
    set value(v: any);
    setValue(args: SetValueArguments<any>): Promise<boolean>;

    /**
     * Continuous-edit transaction for draggy editors (color picker, sliders,
     * vector fields). The PROPERTY snapshots its own pre-edit value, streams
     * intermediate {@link previewValue} writes that don't touch history, and
     * {@link commitEdit} folds the whole gesture into ONE undo step
     * (pre-edit → final). Because the snapshot and the final value are read
     * through the SAME accessor, history and the changeset never see a
     * representation mismatch — which is exactly why an editor should NOT
     * clone/snapshot the value itself (that produced e.g. `Old: 0` vs
     * `New: {r,g,b}` for a color).
     *
     * Usage: `beginEdit()` once → `previewValue(v)` per tick → `commitEdit(v)`
     * on release (or `cancelEdit()` to abort). `commitEdit` with no preceding
     * `beginEdit` behaves like a plain {@link setValue}.
     */
    beginEdit(): void;
    /**
     * Live intermediate write during an edit — applied, not recorded in history.
     * Fires `onDidChange` so other bound UI updates live; pass `{ silent: true }`
     * to skip the notification (e.g. a native color picker that must not be
     * repainted mid-gesture).
     */
    previewValue(newValue: any, opts?: { silent?: boolean }): Promise<boolean>;
    /** End the edit: one undo step from the beginEdit snapshot to `finalValue` (or the current value). */
    commitEdit(finalValue?: any): Promise<boolean>;
    /** Abort the edit: revert to the beginEdit snapshot, no history entry. */
    cancelEdit(): Promise<boolean> | void;

    /** Compares the given value to the current property value.
     * If oldValue is provided, it is used for comparison instead of the current property value.
     */
    equalsValue(value: any, oldValue?: any): boolean;
    cloneValue(): any;
    convertValue<T extends unknown>(usage: ConverterContext): Promise<T | null>;

    getTooltip?: () => string | null;

    /**
     * Optional guard checked BEFORE a user edit is applied (the value setter /
     * `setValue` → `SetPropertyCommand` path). Return `false` to veto the edit;
     * the guard owns any side effect (e.g. prompting login). Focus and in-field
     * interaction are unaffected — only applying the edit is gated. It is NOT
     * consulted on the internal `withoutNotify` write the command uses to apply.
     */
    applyGuard?: () => boolean;
}

export function isPropertyLike(obj: any): obj is IProperty {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const maybeProperty = obj as IProperty;
    return typeof maybeProperty.key === "string"
        && typeof maybeProperty.type === "string"
        && typeof maybeProperty.editable === "boolean"
        && typeof maybeProperty.hidden === "boolean"
        && 'value' in maybeProperty;
}



export interface IPropertyWithType<T> extends IProperty {
    get value(): T;
    set value(v: T);
    setValue(args: SetValueArguments<T>): Promise<boolean>;
}

export interface IPropertyWithEnumOptions extends IProperty {
    readonly metadata: Required<Pick<PropertyMetadata, "enumOptions">> & PropertyMetadata;
}
export function isPropertyWithEnumOptions(obj: IProperty): obj is IPropertyWithEnumOptions {
    return obj.metadata.enumOptions !== undefined && Array.isArray(obj.metadata.enumOptions);
}



export class Property implements IProperty {

    readonly owner: object;
    readonly key: string;
    readonly type: IProperty["type"];
    readonly metadata: PropertyMetadata;

    /** See {@link IProperty.applyGuard}. Return false to veto an edit before apply. */
    applyGuard?: () => boolean;

    get editable(): boolean {
        return this.metadata?.editable !== false;
    }
    set editable(value: boolean) {
        if (this.metadata.editable === value) return;
        this.metadata.editable = value;
        this.notifyChanged();
    }

    get readable(): boolean {
        return this.metadata?.readable !== false;
    }
    get writeable(): boolean {
        return this.metadata?.writeable !== false;
    }

    get hidden() {
        return this.metadata?.hidden === true;
    }
    set hidden(value: boolean) {
        if (this.metadata.hidden === value) return;
        this.metadata.hidden = value;
        // Visibility bookkeeping, not a value change: every collection rebuild
        // toggles `hidden` on when-gated rows, so an unflagged notify here
        // spams value listeners (edit registration, texture version bumps)
        // once per visible row per rebuild.
        this.notifyChanged({ hiddenOnly: true });
    }

    /** Used to actually read/write object values (e.g. complex objects) */
    accessorService: IPropertyAccessorService;
    equalityComparer: IEqualityComparer | null;
    valueConverter: IValueConverter | null;

    label: string = "";
    children?: IProperty[];

    constructor(
        owner: object,
        key: string,
        type: string,
        metadata?: Partial<PropertyMetadata> | null,
        accessor: IPropertyAccessorService | null = null,
        converter: IValueConverter | null = null,
        comparer: IEqualityComparer | null = null,
        readonly readDefinition: PropertyReadDefinition = memberReadDefinition,
    ) {
        this.owner = owner;
        this.key = key;
        this.label = key;
        this.type = type;

        this.accessorService = accessor || PropertyAccessorService.Main;
        this.equalityComparer = comparer || PropertyComparisonService;
        this.valueConverter = converter || ValueConversionService;

        this.metadata = {
            editable: true,
            ...metadata
        }
        if (this.metadata.initialValue === undefined) {
            try {
                this.metadata.initialValue = this.value;
            }
            catch {
                // Ignore errors reading initial value (e.g., proxy invariant violations)
                this.metadata.initialValue = undefined;
            }
        }

        // try to narrow type
        if (this.type === "object") {
            if (isColorPropertyLike(this)) {
                // @ts-ignore
                this.type = "color";
            }
            // else if(isVectorLike(this)) {
            //     this.type = "vector";
            // }
        }

        if (this.label.endsWith("needle")) {
            this.label = this.label.substring(0, this.label.length - "needle".length);
        }
        while (this.label.length > 0 && this.label.endsWith("_")) {
            this.label = this.label.substring(0, this.label.length - 1);
        }
        while (this.label.length > 0 && this.label.startsWith("_")) {
            this.label = this.label.substring(1);
        }
    }

    get value(): unknown {
        try {
            return this.readDefinition.read(this, this.accessorService);
        }
        catch (err) {
            // Catch Proxy invariant violations that can occur when the owner object
            // is wrapped in a proxy (e.g., Threlte/Svelte reactivity) and the property
            // is non-configurable with an object value
            if (isProxyInvariantError(err)) {
                return undefined;
            }
            throw err;
        }
    }

    set value(v: any) {
        this._validateWriteValue(v);
        v = this._convertValueToType(v);

        const current = (this.owner as any)[this.key];
        if (current === v) return;

        // Gate applying the edit (not focus/interaction). The guard owns the side
        // effect (e.g. prompting login) and returns false to veto.
        if (this.applyGuard && !this.applyGuard()) {
            // Vetoed: no write happens, but a bound control may already show the
            // attempted value optimistically — re-emit the current (unchanged)
            // value so the UI repaints back to the truth.
            this._onDidChange.fire({ property: this, value: current });
            return;
        }

        // Delegate to command system for proper undo/redo and change propagation
        const commands = CommandService.get();
        if (commands) {
            commands.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                property: this,
                value: v
            });
        }
    }

    valueNotDefault(): boolean | undefined {
        if (this.metadata?.defaultValue === undefined) {
            return undefined;
        }
        return !this.equalsValue(this.metadata.defaultValue);
    }

    // private waitForWriteCompletion(): Promise<void> {
    //     return this._currentWritePromise?.catch(() => {/** ignore */ }) || Promise.resolve();
    // }
    private _currentWritePromise: Promise<boolean> | null = null;

    async setValue(args: SetValueArguments<any>): Promise<boolean> {

        // Gate applying a user edit at the start — this catches even the live
        // `withoutUndo`/`withoutNotify` writes editors make while dragging/typing,
        // not just the final commit. The command's own apply/undo pass
        // `skipApplyGuard`. The guard owns the side effect (e.g. login dialog).
        if (!args.skipApplyGuard && this.applyGuard && !this.applyGuard()) {
            // Vetoed (e.g. login required): re-emit the current (unchanged) value
            // so any control showing the attempted value optimistically repaints
            // back to the truth. See the `value` setter above.
            this._onDidChange.fire({ property: this, value: (this.owner as any)[this.key] });
            return false;
        }


        let v = args.newValue;
        this._validateWriteValue(v);
        v = this._convertValueToType(v);

        if (args.withoutNotify || args.withoutUndo) {
            // Directly set value without notifying change system
            const oldValue = args.oldValue;
            return this._currentWritePromise = this.accessorService.writeValue(this, v, args.metadata, args.usageHint).then(() => {
                if (args.withoutNotify !== true) {
                    // withoutUndo write = an intermediate PREVIEW (previewValue /
                    // live drag) — tag it transient so expensive consumers
                    // (recompile, changeset) wait for the commit
                    this._onDidChange.fire({ property: this, value: v, previousValue: oldValue, transient: true });
                }
                return true;
            })
        }
        else {
            const commands = CommandService.get();
            if (commands) {
                // The property command will handle notifying change system and invoke onDidChange
                return commands.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                    property: this,
                    value: args.newValue,
                    previousValue: args.oldValue,
                    metadata: args.metadata,
                    usageHint: args.usageHint
                }).then(res => res.success);
            }
        }

        return false;
    }

    // #region continuous-edit transaction (beginEdit / previewValue / commitEdit)
    private _editActive = false;
    private _editSnapshot: any = undefined;

    beginEdit(): void {
        this._editSnapshot = this._cloneForEdit(this.value);
        this._editActive = true;
    }

    /**
     * Snapshot that is BOTH deep (survives the in-place edits `previewValue`
     * makes) AND type-preserving (serializes like the live value the changeset
     * reads for the new value, so old & new match). Math value-types
     * (`Color`/`Vector`/`Matrix`/`Euler`/`Quaternion`) self-clone → an
     * independent, prototyped copy that keeps e.g. `Color.toJSON` → hex.
     * `structuredClone` is wrong here (strips prototype/toJSON) and the
     * accessor's `cloneValue` shares a `Matrix`'s `elements` array — hence the
     * value's own `.clone()`.
     *
     * Heavy GPU/scene resources (textures, materials, geometries, objects) are
     * NEVER cloned: their `.clone()` is expensive + side-effectful (new uuid,
     * GPU re-upload), and they are reassigned (not mutated in place), so the
     * live reference is already a valid snapshot. (These don't reach this path
     * today — the continuous-edit API is only used by numeric/color/vector/
     * matrix inputs — the guard just keeps it safe if that changes.)
     */
    private _cloneForEdit(value: any): any {
        if (value === null || typeof value !== "object") return value; // primitives
        const v = value as { isTexture?: boolean; isMaterial?: boolean; isBufferGeometry?: boolean; isObject3D?: boolean };
        if (v.isTexture || v.isMaterial || v.isBufferGeometry || v.isObject3D) {
            return value; // reference snapshot — don't clone heavy resources
        }
        if (typeof (value as { clone?: unknown }).clone === "function") {
            try {
                return (value as { clone(): unknown }).clone();
            }
            catch {
                // fall through to the accessor clone
            }
        }
        return this.cloneValue();
    }

    previewValue(newValue: any, opts?: { silent?: boolean }): Promise<boolean> {
        // live write, applied but not recorded in history — the gesture is
        // coalesced into the single commit step. Default fires onDidChange (so
        // other bound UI tracks the drag); `silent` skips it (withoutNotify) for
        // editors whose own control must not be repainted mid-gesture.
        return opts?.silent
            ? this.setValue({ newValue, withoutNotify: true })
            : this.setValue({ newValue, withoutUndo: true });
    }

    async commitEdit(finalValue?: any): Promise<boolean> {
        const oldValue = this._editActive ? this._editSnapshot : undefined;
        this._editActive = false;
        this._editSnapshot = undefined;
        const newValue = finalValue !== undefined ? finalValue : this.value;
        // one undo step: snapshot → final. With no preceding beginEdit, oldValue
        // is undefined and SetPropertyCommand snapshots the current value itself.
        return this.setValue({ newValue, oldValue, withoutNotify: false });
    }

    cancelEdit(): Promise<boolean> | void {
        if (!this._editActive) return;
        const snapshot = this._editSnapshot;
        this._editActive = false;
        this._editSnapshot = undefined;
        if (snapshot === undefined) return;
        // revert live, without a history entry
        return this.setValue({ newValue: snapshot, withoutUndo: true });
    }

    // #endregion

    equalsValue(input: any, oldValue?: any): boolean {
        // If oldValue is not provided, use current value to compare against the input provided
        oldValue = oldValue === undefined ? this.value : oldValue;
        if (this.equalityComparer) {
            return this.equalityComparer.areEqual(input, oldValue);
        }
        else {
            return input === oldValue;
        }
    }

    cloneValue(): any {
        return this.accessorService.cloneValue(this);
    }

    async convertValue<T extends unknown>(intent: ConverterContext): Promise<T | null> {
        if (!this.valueConverter) {
            return null;
        }

        // await this.waitForWriteCompletion();

        try {
            return this.valueConverter.convert<T>(this.value, intent).catch(err => {
                console.error("Error converting property value:", err);
                return null;
            })
        }
        catch (err) {
            console.error("Error converting property value:", err);
            return null;
        }
    }

    private readonly _onDidChange = new Emitter<PropertyChangedEvent>();
    readonly onDidChange = this._onDidChange.event;

    notifyChanged(args?: Omit<PropertyChangedEvent, "property">): void {
        this._onDidChange.fire({ ...args, property: this, });
    }

    private _validateWriteValue(newValue: unknown): void {
        if (!this.editable) {
            throw new Error(`Property ${this.key} is not editable.`);
        }
        if (this.metadata?.static === true) {
            throw new Error(`Property ${this.key} is static and cannot be changed.`);
        }
        if (!this.writeable) {
            // if the accessor indicates that the property is writeable (e.g. write in place) we allow it
            // const accessor = this.accessorService?.getAccessorWithReadAbility({ property: this });
            // if (accessor?.isWriteable?.(this.owner, this.key) === true) {
            //     return;
            // }
            if (this.accessorService.canWrite(this, newValue) === true) {
                return;
            }
            throw new Error(`Property ${this.key} is not writeable.`);
        }
    }

    private _convertValueToType(v: any): any {
        switch (this.type) {
            case "number":
                return Number(v);
            case "boolean":
                return Boolean(v);
            case "string":
                return String(v);
            default:
                return v;
        }
    }
}

/**
 * Returns the property's children WITHOUT forcing lazy children to materialize.
 * Providers that install lazy children expose a `peekChildren` accessor for this.
 */
export function peekPropertyChildren(property: IProperty): IProperty[] | undefined {
    const peek = (property as { peekChildren?: () => IProperty[] | undefined }).peekChildren;
    if (peek) return peek.call(property);
    return property.children;
}

const _materializationListeners = new Set<(children: IProperty[]) => void>();
/**
 * Fired by providers when lazily-created child properties are materialized
 * (e.g. when the inspector expands a nested group). Allows services that hook
 * up per-property listeners at collection creation time to also cover
 * properties that come into existence later.
 */
export function onPropertyChildrenMaterialized(listener: (children: IProperty[]) => void): IDisposable {
    _materializationListeners.add(listener);
    return { dispose: () => _materializationListeners.delete(listener) };
}
export function notifyPropertyChildrenMaterialized(children: IProperty[]): void {
    for (const listener of _materializationListeners) {
        try { listener(children); } catch { /* ignore */ }
    }
}

export function* iterateProperties(properties: Iterable<IProperty>, options?: { materialize?: boolean }): Iterable<IProperty> {
    // Iterative DFS (preorder) with an explicit stack. The previous recursive
    // `yield*` delegation made every yielded property bubble through the whole
    // generator chain — O(depth) per property, i.e. quadratic on deep property
    // trees — which dominated selection time for deeply nested objects.
    //
    // With `materialize: false` the walk does not force lazily-built children
    // (see peekPropertyChildren) — used by hot paths that run on selection.
    const materialize = options?.materialize !== false;
    const stack: Iterator<IProperty>[] = [properties[Symbol.iterator]()];
    while (stack.length > 0) {
        const res = stack[stack.length - 1].next();
        if (res.done) {
            stack.pop();
            continue;
        }
        const property = res.value;
        yield property;
        const children = materialize ? property.children : peekPropertyChildren(property);
        if (children?.length) {
            stack.push(children[Symbol.iterator]());
        }
    }
}


// #region PropertyReader






// #region Collections

/**
 * A collection of property groups
 */
export interface IPropertyCollection extends IDisposable {
    name?: string;
    readonly groups: IPropertyGroup[];
}


/**
 * A group of properties
 */
export interface IPropertyGroup {
    id: string;
    name: string;
    expanded: boolean;
    icon: "object" | "material" | "component" | ({} & string);

    description?: string;
    tooltip?: string;

    readonly categories: string[];
    readonly properties: IProperty[];
    readonly actions: IAction[];
    readonly attachments: IAttachment[];
    readonly onDidChange: Event<{ expanded?: boolean }>;
}


export class PropertyGroup implements IPropertyGroup {

    icon: IPropertyGroup["icon"] = "object";

    private readonly _onDidChange = new Emitter<{ expanded?: boolean }>();
    readonly onDidChange = this._onDidChange.event;

    constructor(
        public readonly id: string,
        public name: string,
        public readonly properties: IProperty[] = [],
        public readonly actions: IAction[] = [],
        public readonly attachments: IAttachment[] = [],
    ) {

    }

    addCategory(...category: string[]): void {
        let changed = false;
        for (const cat of category) {
            if (!this.categories.includes(cat)) {
                this.categories.push(cat);
                changed = true;
            }
        }
        if (changed) this._onDidChange.fire({});
    }

    readonly categories: string[] = [];

    notifyChanged() {
        this._onDidChange.fire({});
    }

    private _expanded: boolean = true;
    get expanded(): boolean {
        return this._expanded;
    }
    set expanded(value: boolean) {
        if (this._expanded !== value) {
            this._expanded = value;
            this._onDidChange.fire({ expanded: value });
        }
    }
}


export class PropertyCollection extends Disposable implements IPropertyCollection {

    static readonly Empty = new PropertyCollection("empty", []);

    name?: string;

    constructor(
        name?: string,
        public readonly groups: IPropertyGroup[] = [],
    ) {
        super();
        this.name = name;
    }

    persistFrom(storage: IStorageService) {
        // Store expanded state in storage
        for (const group of this.groups) {
            if (group.id?.length) {
                const expanded = storage.getBoolean(`properties.${group.id}.expanded`, StorageScope.APPLICATION);
                group.expanded = expanded ?? true;
            }

            this._register(group.onDidChange(({ expanded }) => {
                if (group.id?.length && typeof expanded === "boolean") {
                    storage.store(`properties.${group.id}.expanded`, expanded, StorageScope.APPLICATION, StorageTarget.MACHINE);
                }
            }));
        }
    }

    watch(contextKeyService: IContextKeyService, logger: ILogService): void {

        const propertiesWithWhen = new Array<IProperty>();
        const propertiesWithKeys = new Map<string, IProperty>();
        const keysSet = new Set<string>();
        const contextKeyExpressions = new Array<ContextKeyExpression>();
        const keysCreated = new Set<string>();

        for (const group of this.groups) {
            // Don't force lazy children here: this runs on every selection and
            // context keys / when-clauses only exist on already-built properties.
            for (const prop of iterateProperties(group.properties, { materialize: false })) {
                // property.metadata.address
                if (prop.metadata.contextKey) {
                    propertiesWithKeys.set(prop.metadata.contextKey, prop);
                    keysSet.add(prop.metadata.contextKey);
                }
                if (prop.metadata.when) {
                    propertiesWithWhen.push(prop);
                    contextKeyExpressions.push(prop.metadata.when!);
                }
            }
        }

        // Collect missing keys and warn once at the end: this loop can visit
        // thousands of keys for large selections, and a console.warn per key
        // froze the UI for seconds when selecting deeply nested objects.
        const missingKeys = new Set<string>();
        for (const expr of contextKeyExpressions) {
            const keys = expr.keys();
            for (const keyName of keys) {
                const prop = propertiesWithKeys.get(keyName);
                if (!prop) {
                    missingKeys.add(keyName);
                }
                else {
                    if (keysCreated.has(keyName)) {
                        continue;
                    }
                    keysCreated.add(keyName);

                    // const isPotentialHeavy = prop.value && typeof prop.value === "object";
                    // if (isPotentialHeavy) logger.debug("PropertyCollection.watch: Potentially heavy property value for context key:", keyName);
                    // else logger.debug("PropertyCollection.watch: Creating Key for", keyName);

                    const key = contextKeyService.createKey(keyName, PropertyCollection.getContextKeyValue(prop.value, prop.metadata) as any);
                    this._register(prop.onDidChange(evt => {
                        if (evt.value !== undefined) {
                            key.set(PropertyCollection.getContextKeyValue(prop.value, prop.metadata) as any);
                        }
                    }));
                }
            }
        }

        if (missingKeys.size > 0) {
            logger.warn(`PropertyCollection.watch: No property found for ${missingKeys.size} context key(s):`, [...missingKeys].join(", "));
        }

        this.updateWhen(propertiesWithWhen, contextKeyService, logger);
        this._register(contextKeyService.onDidChangeContext((e) => {
            if (e.affectsSome(keysSet)) {
                this.updateWhen(propertiesWithWhen, contextKeyService, logger);
            }
        }));

    }

    /** Shared by live watchers and addressed inspection snapshots. */
    static getContextKeyValue(value: unknown, metadata?: PropertyMetadata): unknown {
        if (metadata?.contextValue) {
            return PropertyCollection.toContextPrimitive(metadata.contextValue(value));
        }
        if (isColorLike(value)) {
            if ("getHex" in value && typeof value.getHex === "function") {
                return value.getHex();
            }
            if ("value" in value) {
                return PropertyCollection.toContextPrimitive((value as any).value);
            }
        }
        return PropertyCollection.toContextPrimitive(value);
    }

    /**
     * Context key values MUST be primitives: the ContextKeyService deep-compares
     * them for change detection (base/common/objects `equals`, which has no cycle
     * guard), and a live three.js object handed in here would (a) blow the stack
     * on its `parent <-> children` / `source` reference cycles and (b) be strongly
     * retained by the context key, against the weak-registry rule.
     *
     * Reduce any object to a stable primitive — prefer its `uuid` so `when` clauses
     * (`!= null`, value-change detection) still react when the object is swapped —
     * else fall back to a presence boolean. Primitives pass through untouched.
     */
    private static toContextPrimitive(value: unknown): unknown {
        if (value === null || value === undefined) return value;
        const type = typeof value;
        if (type === "string" || type === "number" || type === "boolean" || type === "bigint") {
            return value;
        }
        const uuid = (value as { uuid?: unknown }).uuid;
        if (typeof uuid === "string") return uuid;
        return true; // present, but opaque to when-clauses
    }

    private updateWhen(props: IProperty[], contextKeyService: IContextKeyService, logger: ILogService | null) {
        for (const prop of props) {
            const when = prop.metadata.when;
            if (when) {
                const match = contextKeyService.contextMatchesRules(when);
                prop.hidden = !match;
                // logger?.debug(`PropertyCollection.updateWhen`, prop.key);
            }
        }

    }
}
